import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import CustomerDashboard from "@/pages/CustomerDashboard";
import DriverDashboard from "@/pages/DriverDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import PolicyPage from "@/pages/PolicyPage";
import ProtectedRoute from "@/components/ProtectedRoute";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// XMLHttpRequest-based fetch to avoid Emergent script conflicts
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; } 
      catch { data = xhr.responseText; }
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(xhr.responseText)
      });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    if (options.body) {
      xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    } else {
      xhr.send();
    }
  });
};

// Log API URL for debugging (only in development)
if (process.env.NODE_ENV === 'development') {
  console.log('API URL:', API);
}

// Auth Context
export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/policies/:slug" element={<PolicyPage />} />
      <Route path="/customer" element={<ProtectedRoute role="customer"><CustomerDashboard /></ProtectedRoute>} />
      <Route path="/driver" element={<ProtectedRoute role="driver"><DriverDashboard /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/super-admin" element={<ProtectedRoute role="super_admin"><SuperAdminDashboard /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("cv_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const res = await safeFetch(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
            credentials: "include",
          });
          if (res.ok) {
            const data = await res.json();
            setUser(data);
          } else {
            localStorage.removeItem("cv_token");
            setToken(null);
          }
        } catch {
          localStorage.removeItem("cv_token");
          setToken(null);
        }
      } else {
        // Try cookie-based auth
        try {
          const res = await safeFetch(`${API}/auth/me`, { credentials: "include" });
          if (res.ok) {
            const data = await res.json();
            setUser(data);
          }
        } catch {}
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = (newToken, userData) => {
    if (newToken) {
      localStorage.setItem("cv_token", newToken);
      setToken(newToken);
    }
    setUser(userData);
  };

  const logout = async () => {
    try {
      await safeFetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } catch {}
    localStorage.removeItem("cv_token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, API }}>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthContext.Provider>
  );
}

export default App;
